import moment from "moment";
import Papa from "papaparse";
import config from "../Config/config";
import { fetchAllCategories } from "./categoryController";
import { rowType } from "../Components/helpers";

const fetchAllProjects = async (
  sortBy,
  sortOrderStart,
  sortOrderEnd,
  sortOrderActivity,
  showMyProjects,
  showMyUnitProjects,
  limit,
  page,
  startDateFrom,
  startDateTo,
  endDateFrom,
  endDateTo,
  setProjects,
  setTotalPages,
  internal,
  searchFields,
  filterFields,
) => {
  let sortOrder =
    sortBy === "start_date"
      ? sortOrderStart
      : sortBy === "end_date"
        ? sortOrderEnd
        : sortOrderActivity;

  const queryParams = new URLSearchParams({
    showMyProjects,
    showMyUnitProjects,
    sortBy,
    sortOrder,
    startDateFrom: startDateFrom || "",
    startDateTo: startDateTo || "",
    endDateFrom: endDateFrom || "",
    endDateTo: endDateTo || "",
    internal,
    searchFields: JSON.stringify(searchFields) || "",
    filterFields: JSON.stringify(filterFields) || "",
    limit,
    page,
  });
  try {
    const response = await fetch(
      `${config.serverBaseUrl}/projects?${queryParams}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      },
    );
    if (response.ok) {
      const resJson = await response.json();
      const data = resJson.data;
      setProjects(data.projects);
      setTotalPages(data.totalPages);
    } else {
      console.error("Failed to fetch projects");
    }
  } catch (error) {
    console.error("Error fetching projects:", error);
  }
};

const fetchAllProgressReportProjects = async (setProjects, setError) => {
  try {
    const response = await fetch(`${config.serverBaseUrl}/projects/progress`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("Projects not found");
    }
    const resJson = await response.json();
    const data = resJson.data;
    setProjects(data);
  } catch (error) {
    console.log("Fetch error:", error);
    setError(error.message);
  }
};

const fetchAllProjectsViewProject = async (
  partialCode,
  setSimilarProjects,
  setError,
) => {
  if (!partialCode) {
    return;
  }
  try {
    const response = await fetch(
      `${config.serverBaseUrl}/projects?sortBy=start_date&sortOrder=DESC&partialCode=${partialCode}&limit=5`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );
    if (!response.ok) {
      throw new Error("Projects not found");
    }
    const resJson = await response.json();
    const data = resJson.data;
    setSimilarProjects(data.projects);
  } catch (error) {
    console.log("Fetch error:", error);
    setError(error.message);
  }
};

const fetchAllProjectsOfManagerAndUnit = async (
  projectManagerEmail,
  unit_id,
  setManagerProjects,
) => {
  try {
    const response = await fetch(
      `${config.serverBaseUrl}/projects/autofillprojects?projectManagerEmail=${projectManagerEmail}&unit_id=${unit_id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );
    if (!response.ok) {
      throw new Error("Projects not found");
    }
    const resJson = await response.json();
    const data = resJson.data;

    setManagerProjects(data.projects);
  } catch (error) {
    console.log("Fetch error:", error);
  }
};

const fetchProject = async (
  uid,
  setForm,
  setBudget,
  setRevenueSharing,
  setCategories,
  setMembers,
  setUploads,
  setError,
) => {
  try {
    const response = await fetch(`${config.serverBaseUrl}/projects/${uid}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error((await response.json())?.message ?? "Project not found");
    }
    const resJson = await response.json();
    const data = resJson.data;
    setForm((prevForm) => ({
      ...prevForm,
      ...data.project,
    }));
    setMembers(data.project.members);
    setBudget(data.project.budgetRows);
    setRevenueSharing(JSON.parse(data.project.revenue_sharing));
    setUploads(
      data.project.attachments?.map((a) => ({
        id: a.attachment_id,
        uid: a.uid,
        file: { name: a.original_name, type: a.mime_type },
        category: a.category,
      })),
    );
    await fetchAllCategories(
      data.project.unit_id,
      data.project.activity_id,
      setCategories,
    );
  } catch (error) {
    console.error("Error fetching project:", error);
    setError(error.message);
  }
};

const fetchProjectForAutofill = async (
  uid,
  setForm,
  setBudget,
  setRevenueSharing,
  setCategories,
  setMembers,
  setProgressReports,
  setError,
) => {
  try {
    const response = await fetch(
      `${config.serverBaseUrl}/projects/autofill/${uid}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error((await response.json())?.message ?? "Project not found");
    }
    const resJson = await response.json();
    const data = resJson.data;
    setForm((prevForm) => ({
      ...prevForm,
      ...data.project,
    }));
    setMembers(data.project.members);
    setBudget(data.project.budgetRows);
    setRevenueSharing(JSON.parse(data.project.revenue_sharing));
    setProgressReports(data.project.progress);
    await fetchAllCategories(
      data.project.unit_id,
      data.project.activity_id,
      setCategories,
    );
  } catch (error) {
    console.error("Error fetching project:", error);
    setError(error.message);
  }
};

const fetchProjectViewProject = async (
  uid,
  setProject,
  setUnit,
  setActivity,
  setStatus,
  setAttachments,
  setError,
) => {
  try {
    const response = await fetch(`${config.serverBaseUrl}/projects/${uid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error((await response.json())?.message ?? "Project not found");
    }

    const resJson = await response.json();
    const data = resJson.data;
    setProject(data.project);
    setUnit(data.project.Unit);
    setActivity(data.project.Activity);
    setStatus(data.project.Status);
    setAttachments(data.project.attachments);
    return data.project;
  } catch (error) {
    console.log("Fetch error:", error);
    setError(error.message);
  }
};

const submitProject = async (
  projectData,
  setResultMessage,
  disableBlock,
  navigate,
) => {
  try {
    const response = await fetch(`${config.serverBaseUrl}/projects/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(projectData),
    });

    if (response.status === 200) {
      const resJson = await response.json();
      const data = resJson.data;

      setResultMessage("Project submitted successfully! Redirecting...");
      disableBlock();
      setTimeout(function () {
        navigate(`/project/${data.project.uid}`);
      }, 1000);
      return data.project;
    } else {
      const resJson = await response.json();
      setResultMessage(
        `Failed to submit project: ${
          resJson.errors
            ? resJson.errors.map((e) => e.message).join(", ")
            : resJson.error
        }`,
      );
    }
  } catch (error) {
    console.error("Error submitting project:", error);
    setResultMessage("Error submitting project:", error);
  }
};

const saveProject = async (
  projectData,
  setResultMessage,
  disableBlock,
  navigate,
) => {
  try {
    const response = await fetch(`${config.serverBaseUrl}/projects/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(projectData),
    });

    if (response.status === 200) {
      const resJson = await response.json();
      const data = resJson.data;

      setResultMessage("Project saved successfully! Redirecting...");
      disableBlock();
      setTimeout(function () {
        navigate(`/project/${data.project.uid}`);
      }, 1000);
      return data.project;
    } else {
      const resJson = await response.json();
      setResultMessage(
        `Failed to save project: ${
          resJson.errors
            ? resJson.errors.map((e) => e.message).join(", ")
            : resJson.error
        }`,
      );
    }
  } catch (error) {
    console.error("Error saving project:", error);
    setResultMessage("Error saving project:", error);
  }
};
const exportProjects = async (
  sortBy,
  sortOrderStart,
  sortOrderEnd,
  sortOrderActivity,
  showMyProjects,
  showMyUnitProjects,
  startDateFrom,
  startDateTo,
  endDateFrom,
  endDateTo,
  searchFields,
  filterFields,
) => {
  try {
    const queryParams = new URLSearchParams({
      showMyProjects,
      showMyUnitProjects,
      sortBy,
      sortOrder:
        sortBy === "start_date"
          ? sortOrderStart
          : sortBy === "end_date"
            ? sortOrderEnd
            : sortOrderActivity,
      startDateFrom: startDateFrom || "",
      startDateTo: startDateTo || "",
      endDateFrom: endDateFrom || "",
      endDateTo: endDateTo || "",
      searchFields: JSON.stringify(searchFields) || "",
      filterFields: JSON.stringify(filterFields) || "",
      limit: -1, // override pagination
    });

    const res = await fetch(`${config.serverBaseUrl}/projects?${queryParams}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });

    if (res.ok) {
      const resJson = await res.json();
      const projects = resJson.data.projects;

      if (!projects || projects.length === 0) {
        alert("No projects to export.");
        return;
      }
      const flattened = projects.map((project) => ({
        project_id: project.project_id,
        code: project.code,
        name: project.name,
        project_manager_name: project.project_manager_name,
        client_name: project.client_name,
        start_date: moment(project.start_date).format("DD.MM.YYYY"),
        end_date: moment(project.end_date).format("DD.MM.YYYY"),
        unit: project.Unit?.unit_name || "",
        activity: project.Activity?.activity_name || "",
        status: project.Status?.status_name || "",
        total_budget: project.budgetRows?.find(
          (r) => r.row_type === rowType.total,
        )?.value,
        updated_at: moment(project.updatedAt).format("DD.MM.YYYY HH:MM"),
      }));

      const csv = Papa.unparse(flattened);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `projects_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      alert("Export failed.");
    }
  } catch (error) {
    console.error("Export error:", error);
    alert("Export failed.");
  }
};

const generateCode = async (uid, setAlert, setError) => {
  try {
    const response = await fetch(
      `${config.serverBaseUrl}/projects/gencode/${uid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (response.ok) {
      const json = await response.json();
      return json.data.projectCode;
    } else {
      const data = await response.json();
      setAlert({ severity: "error", message: `${data.error}` });
      setError(false);
    }
  } catch (error) {
    console.error("Error generating project code:", error);
    setAlert({ severity: "error", message: `${error}` });
  }
};

const saveCode = async (
  project,
  code,
  serialNumber,
  syncToMyHours,
  syncToJoosep,
  setAlert,
  setEditCode,
  refreshProject,
  setCodeFields,
  setError,
) => {
  try {
    const response = await fetch(`${config.serverBaseUrl}/projects/code`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        project_id: project.project_id,
        code: code,
        serial_number: serialNumber,
        syncToMyHours,
        syncToJoosep,
      }),
    });

    if (response.ok) {
      setAlert({
        severity: "success",
        message: "Project code updated successfully!",
      });
      setEditCode(false);
      refreshProject();
    } else {
      const data = await response.json();
      setAlert({ severity: "error", message: `${data.error}` });
      setEditCode(false);
      refreshProject();
      setCodeFields(project.code, project.unit_id, project.activity_id);
      setError(false);
    }
  } catch (error) {
    console.error("Error updating project code:", error);
    setAlert({ severity: "error", message: `${error}` });
  }
};

const validateCode = async (code, project_id, setAlert) => {
  try {
    const response = await fetch(
      `${config.serverBaseUrl}/projects/code/validate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          code,
          project_id,
        }),
      },
    );

    if (response.ok) {
      const json = await response.json();
      return json.data.valid;
    } else {
      const data = await response.json();
      console.log(data.errors);
      setAlert({
        severity: "error",
        message: `${
          data.error ?? data.errors.map((e) => e.message)?.join(", ")
        }`,
      });
    }
  } catch (error) {
    console.error("Error updating project code:", error);
    setAlert({ severity: "error", message: `${error}` });
  }
};

const updateProjectStatus = async (uid, setAlert, refreshProject) => {
  try {
    const response = await fetch(
      `${config.serverBaseUrl}/projects/status/${uid}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      },
    );

    if (response.ok) {
      setAlert({
        severity: "success",
        message: "Project status updated successfully",
      });
      refreshProject();
    } else {
      setAlert({
        severity: "error",
        message: "Failed to update project status",
      });
    }
  } catch (error) {
    console.error("Error updating project status:", error);
    setAlert({ severity: "error", message: "Error updating project status" });
  }
};

const fetchProjectManagers = async (setProjectManagers) => {
  await fetch(`${config.serverBaseUrl}/projects/managers`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  })
    .then((res) => res.json())
    .then((resJson) => setProjectManagers(resJson.data.projectManagers));
};

const updateProjectProgress = async (
  project_progress_id,
  project_id,
  progress,
  comments,
  setAlert,
) => {
  try {
    const response = await fetch(`${config.serverBaseUrl}/projects/progress`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        project_progress_id,
        project_id,
        progress,
        comments,
      }),
    });

    if (response.ok) {
      setAlert({
        severity: "success",
        message: "Project progress updated successfully",
      });
      return true;
    } else {
      setAlert({
        severity: "error",
        message: "Failed to update project progress",
      });
    }
  } catch (error) {
    setAlert({ severity: "error", message: "Error updating project status" });
  }
};

const deleteProjectProgress = async (project_progress_id, setAlert) => {
  try {
    const response = await fetch(
      `${config.serverBaseUrl}/projects/progress/${project_progress_id}`,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      },
    );

    if (response.ok) {
      setAlert({
        severity: "success",
        message: "Project progress deleted successfully",
      });
      return true;
    } else {
      setAlert({
        severity: "error",
        message: "Failed to delete project progress",
      });
    }
  } catch (error) {
    setAlert({ severity: "error", message: "Error deleting project status" });
  }
};
const saveFiles = async (uid, attachmentsWithCategory, setAlert) => {
  try {
    const fd = new FormData();

    attachmentsWithCategory
      .filter((f) => f.deleted === true)
      .forEach((a) => {
        fd.append(`deleted`, a.uid);
      });
    attachmentsWithCategory
      .filter((f) => f.uid === undefined)
      .forEach(({ file, category }, i) => {
        fd.append(`category[${i}]`, category); // file-specific category must add before file
        fd.append("files", file, file.name); // multiple files under same key
      });

    const response = await fetch(
      `${config.serverBaseUrl}/projects/attachments/${uid}`,
      {
        method: "POST",
        body: fd,
        credentials: "include",
      },
    );

    if (response.ok) {
      setAlert({
        severity: "success",
        message: "Project files uploaded successfully",
      });
      return true;
    } else {
      setAlert({
        severity: "error",
        message: "Failed to upload project files",
      });
    }
  } catch (error) {
    console.error("Error uploading project files:", error);
    setAlert({ severity: "error", message: "Error uploading project files" });
  }
};

const downloadFile = async (uid, attuid, filename, setAlert) => {
  try {
    window.open(
      `${config.serverBaseUrl}/projects/attachments/${uid}/${attuid}/download/${filename}`,
      "_blank",
      "noopener,noreferrer",
    );
  } catch (error) {
    console.error("Error downloading project file:", error);
    setAlert({ severity: "error", message: "Error downloading project file" });
  }
};

const downloadContractFiles = async (setAlert) => {
  try {
    window.open(
      `${config.serverBaseUrl}/projects/attachments/zip`,
      "_blank",
      "noopener,noreferrer",
    );
  } catch (error) {
    console.error("Error downloading project file:", error);
    setAlert({
      severity: "error",
      message: "Error downloading contract files",
    });
  }
};

const getMyHoursProject = async (oldCode, newCode, setAlert) => {
  try {
    if (!oldCode && !newCode) return false;
    const response = await fetch(`${config.serverBaseUrl}/projects/myhours`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        oldCode,
        newCode,
      }),
    });

    if (response.ok) {
      const json = await response.json();
      return json.data.projects;
    } else {
      const data = await response.json();
      setAlert({
        severity: "error",
        message: `${
          data.error ?? data.errors.map((e) => e.message)?.join(", ")
        }`,
      });
      return false;
    }
  } catch (error) {
    console.error("Error getting project from My Hours:", error);
    setAlert({ severity: "error", message: `${error}` });
    return false;
  }
};

const getJoosepProject = async (oldCode, newCode, setAlert) => {
  try {
    if (!oldCode && !newCode) return false;
    const response = await fetch(`${config.serverBaseUrl}/projects/joosep`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        oldCode,
        newCode,
      }),
    });

    if (response.ok) {
      const json = await response.json();
      return json.data.projects;
    } else {
      const data = await response.json();
      setAlert({
        severity: "error",
        message: `${
          data.error ?? data.errors.map((e) => e.message)?.join(", ")
        }`,
      });
      return false;
    }
  } catch (error) {
    console.error("Error getting project from My Hours:", error);
    setAlert({ severity: "error", message: `${error}` });
    return false;
  }
};

const getTimeEditTermCategories = async (
  setTimeEditTermCategories,
  setAlert,
) => {
  try {
    const response = await fetch(
      `${config.serverBaseUrl}/timeedit/categories`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (response.ok) {
      const json = await response.json();
      setTimeEditTermCategories(json.data.data);
    } else {
      const data = await response.json();
      setAlert({
        severity: "error",
        message: `${
          data.error ?? data.errors.map((e) => e.message)?.join(", ")
        }`,
      });
    }
  } catch (error) {
    console.error("Error getting project from My Hours:", error);
    setAlert({ severity: "error", message: `${error}` });
  }
};

export {
  fetchAllProjects,
  exportProjects,
  fetchProject,
  saveProject,
  submitProject,
  fetchAllProjectsViewProject,
  fetchProjectViewProject,
  generateCode,
  saveCode,
  updateProjectStatus,
  deleteProjectProgress,
  fetchAllProgressReportProjects,
  validateCode,
  updateProjectProgress,
  fetchProjectManagers,
  fetchAllProjectsOfManagerAndUnit,
  fetchProjectForAutofill,
  saveFiles,
  downloadFile,
  downloadContractFiles,
  getMyHoursProject,
  getJoosepProject,
  getTimeEditTermCategories,
};
